/**
 * Persists debug spawn source across reload so admin premium bypass survives /freegame refresh.
 * Cleared when starting a normal (non-debug) game or leaving the game.
 */
import { getDebugSpawnSourceKey } from '~/app/storageKeys';
import { getCurrentStorageUserScopeId } from '~/app/storageScope';
import type { DebugSpawnSource } from '~/game/actions';

const VALID_SOURCES: ReadonlySet<DebugSpawnSource> = new Set(['snapshot', 'common']);

const canUseStorage = (): boolean => typeof window !== 'undefined';

const resolveScopeKey = (userScopeId?: string): string | undefined => {
  const scope = userScopeId ?? getCurrentStorageUserScopeId();
  return scope && scope.length > 0 ? scope : undefined;
};

const isDebugSpawnSource = (value: string): value is DebugSpawnSource =>
  VALID_SOURCES.has(value as DebugSpawnSource);

export const persistDebugSpawnSource = (
  source: DebugSpawnSource | null | undefined,
  userScopeId?: string,
): void => {
  if (!canUseStorage() || !source || !VALID_SOURCES.has(source)) return;
  const scope = resolveScopeKey(userScopeId);
  if (!scope) return;
  window.sessionStorage.setItem(getDebugSpawnSourceKey(scope), source);
};

export const getPersistedDebugSpawnSource = (
  userScopeId?: string,
): DebugSpawnSource | null => {
  if (!canUseStorage()) return null;
  const scope = resolveScopeKey(userScopeId);
  if (!scope) return null;
  const raw = window.sessionStorage.getItem(getDebugSpawnSourceKey(scope));
  if (!raw || !isDebugSpawnSource(raw)) return null;
  return raw;
};

export const clearPersistedDebugSpawnSource = (userScopeId?: string): void => {
  if (!canUseStorage()) return;
  const scope = resolveScopeKey(userScopeId);
  if (!scope) return;
  window.sessionStorage.removeItem(getDebugSpawnSourceKey(scope));
};

export const resolveEffectiveDebugSpawnSource = (
  reduxSource: DebugSpawnSource | null | undefined,
  userScopeId?: string,
): DebugSpawnSource | null => reduxSource ?? getPersistedDebugSpawnSource(userScopeId);
